import { useQuery } from '@tanstack/react-query';
import { ApiError, createApiClient, STEP_UP_REQUIRED_CODE } from '../../api/client';
import { useAccessToken } from '../../auth/useAuth';
import { queryDisplayState } from '../../lib/queryState';
import { workspaceKey } from '../../lib/workspaceQueryKey';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { agreementQueryOptions } from './agreementQuery';
import { levelFromRoles, type CashFlowLevel } from './cashFlowBoard';

/** Months the boards ask for: the façade's default, and the length of the month strip. */
export const BOARD_MONTHS = 12;

/** A plain 403 means "not available". A STEP_UP_REQUIRED 403 is the house step-up flow's, not a refusal. */
const isRefusal = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 403 && err.code !== STEP_UP_REQUIRED_CODE;
const noRetryOn403 = (count: number, err: unknown) => !isRefusal(err) && count < 2;

export type BoardState<T> =
  | { kind: 'loading' }
  | { kind: 'offline' }
  | { kind: 'error'; retry: () => void }
  /** Cash flow is off, or never set up. */
  | { kind: 'off' }
  /** The viewer is at level Nothing, or the façade refused with 403: not available, never an error banner. */
  | { kind: 'nothing' }
  | { kind: 'ready'; data: T; level: CashFlowLevel; retry: () => void };

/**
 * The read behind both boards. `path` is `/cash-flow/summary` or `/cash-flow/entity/{key}`.
 *
 * A viewer at level Nothing never calls the detail read (they hold no `cash-flow:read`). To tell
 * "cash flow is off" from "you see nothing" for them, the agreement is read instead: an admin at
 * Nothing can read it, a plain member cannot (403), which reads as `nothing`. A 403 from the board
 * read itself is also `nothing`. UX only: appcore enforces every read.
 */
export function useCashFlowBoard<T extends { status: 'on' | 'off' }>(
  name: 'cash-flow-summary' | 'cash-flow-entity',
  path: string,
  keyPart: string,
): BoardState<T> {
  const token = useAccessToken();
  const { activeWorkspaceId, activeMembership } = useWorkspace();
  const level = levelFromRoles(activeMembership?.roleIds ?? []);
  const api = createApiClient(token!);
  const ready = !!token && !!activeWorkspaceId && !!activeMembership;

  const dataQ = useQuery({
    queryKey: workspaceKey(name, activeWorkspaceId, keyPart, BOARD_MONTHS),
    queryFn: () => api.get<T>(`${path}?months=${BOARD_MONTHS}`),
    enabled: ready && level !== 'none',
    retry: noRetryOn403,
  });
  const agreementQ = useQuery({
    ...agreementQueryOptions(api, activeWorkspaceId, ready && level === 'none'),
    retry: noRetryOn403,
  });

  if (!ready) return { kind: 'loading' };
  if (level === 'none') {
    if (agreementQ.isPending) return queryDisplayState([agreementQ], false) === 'offline' ? { kind: 'offline' } : { kind: 'loading' };
    if (agreementQ.isError) {
      return isRefusal(agreementQ.error) ? { kind: 'nothing' } : { kind: 'error', retry: () => { void agreementQ.refetch(); } };
    }
    return agreementQ.data?.status === 'on' ? { kind: 'nothing' } : { kind: 'off' };
  }
  if (dataQ.isError && isRefusal(dataQ.error)) return { kind: 'nothing' };
  const state = queryDisplayState([dataQ], false);
  if (state === 'loading') return { kind: 'loading' };
  if (state === 'offline') return { kind: 'offline' };
  if (state === 'error') return { kind: 'error', retry: () => { void dataQ.refetch(); } };
  const data = dataQ.data!;
  if (data.status === 'off') return { kind: 'off' };
  return { kind: 'ready', data, level, retry: () => { void dataQ.refetch(); } };
}
